import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Linkedin, Instagram, Facebook, ArrowRight, ShieldCheck, Lock, CreditCard } from "lucide-react";
import { useMarketConfig } from "@/utils/marketConfig";
import { useAuth } from "@/context/AuthContext";

// Decorative mini score-ring — the product's signature, echoed in the footer.
// Shows "?" instead of a number: your score is the unknown the CTA answers.
function MiniRing({ value = 92 }) {
  const R = 26, C = 2 * Math.PI * R;
  return (
    <svg viewBox="0 0 64 64" className="w-14 h-14 -rotate-90 shrink-0" aria-hidden="true">
      <defs>
        <linearGradient id="footerRing" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6B5CE6" />
          <stop offset="40%" stopColor="#9A59C4" />
          <stop offset="68%" stopColor="#C75C8E" />
          <stop offset="100%" stopColor="#F0734E" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="6" />
      <circle cx="32" cy="32" r={R} fill="none" stroke="url(#footerRing)" strokeWidth="6"
        strokeDasharray={C} strokeDashoffset={C - (value / 100) * C} strokeLinecap="round" />
      <text x="32" y="32" transform="rotate(90 32 32)" textAnchor="middle" dominantBaseline="central"
        fill="white" fontSize="20" fontWeight="700" fontFamily="JetBrains Mono, monospace">?</text>
    </svg>
  );
}

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { cfg: mkt } = useMarketConfig(); // localize the market-fit tagline

  // Context-aware CTA. From any other page → go to /intake. But when the user is
  // ALREADY on /intake, re-navigating just spams history and feels like a reload —
  // instead smooth-scroll to the top and focus the Years field (the first input).
  const goAnalyze = () => {
    if (location.pathname === "/intake") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      const el = document.querySelector('[data-testid="intake-years"]');
      if (el) setTimeout(() => el.focus({ preventScroll: true }), 400);
    } else {
      navigate("/intake");
    }
  };

  return (
    <footer className="relative bg-[#0A1128] text-white overflow-hidden">
      {/* ambient decoration */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#2832C2]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-24 w-96 h-96 rounded-full bg-[#9A59C4]/10 blur-3xl pointer-events-none" />

      {/* ── CTA strip ─────────────────────────────────────────────────── */}
      <div className="relative border-b border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-16 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <MiniRing />
            <div>
              <p className="font-['Chivo'] text-xl sm:text-2xl font-bold leading-snug">
                Your next role is one honest score away.
              </p>
              <p className="text-white/50 text-sm mt-1">Free, deterministic, and built for the {mkt.market === "IN" ? "Indian" : "modern"} job market.</p>
            </div>
          </div>
          <button
            onClick={goAnalyze}
            className="shrink-0 inline-flex items-center gap-2 text-white font-bold text-sm px-6 py-3.5 rounded-xl border-0 cursor-pointer transition-transform hover:-translate-y-0.5"
            style={{ background: "linear-gradient(108deg,#6B5CE6 0%,#9A59C4 36%,#C75C8E 64%,#F0734E 100%)", boxShadow: "0 10px 30px -8px rgba(124,74,180,0.5)" }}
            data-testid="footer-cta"
          >
            Get my free score <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Link columns ──────────────────────────────────────────────── */}
      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-16 py-12">
        <div className="grid grid-cols-2 md:grid-cols-[2fr_1fr_1fr] gap-10 lg:gap-20">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              {/* Exact navbar logo, full colour, no backing. */}
              <img src="/logo.png" alt="PlacementOS" className="h-9 w-auto" />
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-5 max-w-xs">
              The Operating System for Professional Success — honest ATS scoring,
              verified resume optimization, and a job feed that matches the real you.
            </p>
            <div className="flex gap-2.5">
              {[
                { href: "https://www.instagram.com/placementos.ai/", Icon: Instagram, label: "Instagram" },
                { href: "https://www.facebook.com/placementosai", Icon: Facebook, label: "Facebook" },
                { href: "https://www.linkedin.com/company/placementosai", Icon: Linkedin, label: "LinkedIn" },
                { href: "mailto:support@placementos.ai", Icon: Mail, label: "Email" },
              ].map(({ href, Icon, label }) => (
                <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" aria-label={label}
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-[#5B3DF5] hover:border-[#5B3DF5] transition-all group">
                  <Icon className="w-4 h-4 text-white/70 group-hover:text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-bold mb-4 text-xs uppercase tracking-[0.15em] bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(108deg,#6B5CE6,#9A59C4 36%,#C75C8E 64%,#F0734E)" }}>Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-white/60 text-sm hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-white/60 text-sm hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/faq" className="text-white/60 text-sm hover:text-white transition-colors">FAQ</Link></li>
              <li><a href="/blog/" className="text-white/60 text-sm hover:text-white transition-colors">Blog</a></li>
              {!user && <li><Link to="/auth?role=recruiter" className="text-white/60 text-sm hover:text-white transition-colors">For Recruiters</Link></li>}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-bold mb-4 text-xs uppercase tracking-[0.15em] bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(108deg,#6B5CE6,#9A59C4 36%,#C75C8E 64%,#F0734E)" }}>Legal</h4>
            <ul className="space-y-2.5">
              <li><Link to="/privacy" className="text-white/60 text-sm hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-white/60 text-sm hover:text-white transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ────────────────────────────────────────────────── */}
      <div className="relative border-t border-white/10">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-16 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">© 2026 PlacementOS. All rights reserved.</p>
          {/* trust badges — sequential, not stacked */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {[
              { Icon: CreditCard, text: "Stripe Secured" },
              { Icon: Lock, text: "256-bit SSL" },
              { Icon: ShieldCheck, text: "GDPR Compliant" },
            ].map(({ Icon, text }) => (
              <span key={text} className="flex items-center gap-2 text-white/50 text-sm whitespace-nowrap">
                <Icon className="w-4 h-4 text-[#16a34a]" /> {text}
              </span>
            ))}
          </div>
          <p className="text-white/50 text-sm">
            Queries? <a href="mailto:support@mail.placementos.ai" className="text-[#8b80f5] hover:underline">support@mail.placementos.ai</a>
          </p>
        </div>
      </div>

      {/* giant watermark */}
      <div aria-hidden="true" className="relative h-16 sm:h-24 overflow-hidden select-none pointer-events-none">
        <p className="absolute inset-x-0 -bottom-6 sm:-bottom-10 text-center font-['Chivo'] font-black tracking-tight text-[18vw] sm:text-[11vw] leading-none text-white/[0.04] whitespace-nowrap">
          PLACEMENTOS
        </p>
      </div>
    </footer>
  );
}
